import { useCallback, useEffect, useMemo, useState } from 'react';
import { dismissNotification, fetchReportData, listenNotifications, markNotificationKeysRead, markNotificationRead } from '../firebase/repo';
import type { AppNotification } from '../types';
import { todayISO } from '../utils/date';
import { buildNotifications, canDismissNotification, unreadNotificationCount, type NotificationState } from '../utils/notifications';
import type { ReportSourceData } from '../utils/reports';

const emptySource: ReportSourceData = {
  transactions: [],
  payments: [],
  plans: [],
  crdr: [],
  budgets: [],
  accounts: [],
  accountLedger: [],
  balanceAudits: [],
  monthlyPeriods: []
};

export function useNotifications(uid?: string) {
  const [states, setStates] = useState<NotificationState[]>([]);
  const [source, setSource] = useState<ReportSourceData>(emptySource);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!uid) {
      setSource(emptySource);
      return;
    }
    setLoading(true);
    try {
      setSource(await fetchReportData(uid));
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    if (!uid) {
      setStates([]);
      setSource(emptySource);
      return undefined;
    }
    const offNotifications = listenNotifications(uid, (rows) => setStates(rows as NotificationState[]));
    refresh();
    return () => offNotifications();
  }, [uid, refresh]);

  const notifications = useMemo(() => buildNotifications(source, todayISO(), states), [source, states]);
  const unreadCount = useMemo(() => unreadNotificationCount(notifications), [notifications]);

  const markRead = useCallback((id: string) => uid ? markNotificationRead(uid, id) : Promise.resolve(), [uid]);
  const markAllRead = useCallback(() => uid ? markNotificationKeysRead(uid, notifications.filter((item) => item.status === 'unread').map((item) => item.dedupeKey || item.id)) : Promise.resolve(), [uid, notifications]);
  const dismiss = useCallback((item: AppNotification) => {
    if (!uid || !canDismissNotification(item)) return Promise.resolve();
    return dismissNotification(uid, item.dedupeKey || item.id, item.priority);
  }, [uid]);

  return { notifications, unreadCount, loading, refresh, markRead, markAllRead, dismiss };
}
